import { createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Link, Outlet, useLocation, useParams } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from '@/components/ui/sidebar';
import { Separator } from '@/components/ui/separator';
import { AppSidebar } from './AppSidebar';
import { NotificationBell } from './NotificationBell';
import { useCompaniesApi } from '@/services/api';
import { t } from '@/i18n';

export interface AppShellContext {
  nip: string;
  companyName: string;
}

// First path segment after :nip → navbar breadcrumb label.
const SECTION_LABEL_KEYS: Record<string, string> = {
  dashboard: 'navigation.dashboard',
  invoices: 'navigation.invoices',
  sales: 'navigation.sales',
  crm: 'navigation.crm',
  fiscal: 'navigation.fiscal',
  projects: 'navigation.projects',
  contractors: 'navigation.contractors',
  magazyn: 'navigation.magazyn',
  mileage: 'navigation.mileage',
  employees: 'navigation.employees',
  hr: 'navigation.hr',
  tags: 'navigation.tags',
  documents: 'navigation.documents',
};

const HeaderActionsSlotContext = createContext<HTMLElement | null>(null);

/**
 * Renders its children into the right side of the pinned top navbar.
 * Page-level actions (upload, match payment, year picker…) live there instead
 * of a separate page-header row, so content starts right below the navbar.
 */
export function HeaderActions({ children }: { children: ReactNode }) {
  const slot = useContext(HeaderActionsSlotContext);
  if (!slot) return null;
  return createPortal(children, slot);
}

export function AppShell() {
  const { nip } = useParams<{ nip: string }>();
  const location = useLocation();
  const [companyName, setCompanyName] = useState('');
  const [actionsSlot, setActionsSlot] = useState<HTMLElement | null>(null);
  const { getCompanyDetail, getCompanies } = useCompaniesApi();

  useEffect(() => {
    if (!nip) return;
    const currentNip = nip;
    let mounted = true;
    async function loadCompanyName() {
      try {
        if (currentNip === 'other') {
          if (mounted) setCompanyName(t('navigation.unassigned'));
          return;
        }
        const detail = await getCompanyDetail(currentNip);
        if (mounted && detail.name) setCompanyName(detail.name);
      } catch {
        try {
          const companies = await getCompanies();
          const company = companies.find((c) => c.nip === currentNip);
          if (mounted && company) setCompanyName(company.name);
        } catch {
          // ignore
        }
      }
    }
    loadCompanyName();
    return () => {
      mounted = false;
    };
  }, [nip, getCompanyDetail, getCompanies]);

  const section = location.pathname.split('/')[2] ?? '';
  const sectionLabel = SECTION_LABEL_KEYS[section]
    ? t(SECTION_LABEL_KEYS[section])
    : null;

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <header className="bg-background/85 supports-[backdrop-filter]:bg-background/60 sticky top-0 z-40 flex h-14 shrink-0 items-center gap-2 border-b px-4 backdrop-blur-md">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="mr-1 h-4" />
          <nav className="flex min-w-0 items-center gap-1.5 text-sm">
            <Link to={`/${nip}`} className="text-muted-foreground hover:text-foreground truncate transition-colors">
              {companyName}
            </Link>
            {sectionLabel && (
              <>
                <ChevronRight className="text-muted-foreground/50 size-3.5 shrink-0" />
                <h1 className="shrink-0 font-medium">
                  <Link to={`/${nip}/${section}`} className="hover:underline">
                    {sectionLabel}
                  </Link>
                </h1>
              </>
            )}
          </nav>
          <div
            ref={setActionsSlot}
            className="ml-auto flex items-center gap-2 pl-2"
          />
          {nip && <NotificationBell nip={nip} />}
        </header>
        <main className="flex-1 overflow-auto p-4 md:p-6">
          <HeaderActionsSlotContext.Provider value={actionsSlot}>
            <Outlet
              context={{ nip: nip || '', companyName } satisfies AppShellContext}
            />
          </HeaderActionsSlotContext.Provider>
        </main>
      </SidebarInset>
    </SidebarProvider>
  );
}
